// Is this page MSAL's own child window? A pure decision, kept apart from
// main.tsx so it can be read and checked without a browser.
//
// ⚠ WHY THIS FILE EXISTS. msal-browser v5 changed how a silent renewal gets its
// answer back. The hidden iframe is sent to the redirect URI — this app's own
// origin (config/auth.ts) — and the PARENT no longer reads the iframe's URL. It
// waits on a BroadcastChannel for the redirect page to call
// `broadcastResponseToMainFrame`; a popup (graphMail.ts falls back to one for
// Mail.Read) works the same way. This app never called it, so from the day it
// was built on v5 (5.8.0, 2026-04-28) until 2026-10-02:
//   1. every hidden-iframe renewal timed out after 10 s (`timed_out`,
//      `redirect_bridge_timeout`), even when Entra had answered with a code.
//      acquireApiToken's redirect-on-any-failure (2026-06-01) hid it behind a
//      full sign-in round trip;
//   2. the whole app booted inside the hidden iframe and called the API. MSAL
//      refuses a token request there (`block_iframe_reload`), but the copy's
//      acquireApiToken still stamps the once-a-minute redirect guard in the
//      sessionStorage it shares with the parent, so the parent's own timeout
//      can find the guard already spent and skip its redirect.
// In app-quality the same gap recursed ~20 iframes deep after a
// conditional-access expiry (AADSTS70044), until Entra blocked the client as a
// request loop (AADSTS50196).

/** The shape of `window` this needs — narrow so a check can hand it a plain object. */
export interface FrameLike {
  self: unknown;
  top: unknown;
  opener: unknown;
  location: { hash: string; search: string };
}

/** True when the URL carries an authorization response: a `state`, plus a `code`
 *  or an `error`. MSAL asks for the fragment response mode, but a query response
 *  is checked too because that choice is the library's, not ours. */
export function hasAuthResponse(hash: string, search: string): boolean {
  for (const raw of [hash, search]) {
    const params = new URLSearchParams(raw.replace(/^[#?]/, ''));
    if (params.has('state') && (params.has('code') || params.has('error'))) return true;
  }
  return false;
}

/**
 * True when this page is MSAL's hidden iframe (or a popup) landing on the
 * redirect URI with an answer. That page's only job is to hand the answer to the
 * window that asked for it. It must never render the app: an app rendered there
 * has a signed-in account and asks for tokens of its own.
 *
 * A top-level page with a response in the URL is the ordinary sign-in redirect
 * coming home; MsalProvider's `handleRedirectPromise` deals with that.
 */
export function isMsalChildWindow(win: FrameLike): boolean {
  const inFrame = win.self !== win.top;
  const inPopup = Boolean(win.opener);
  return (inFrame || inPopup) && hasAuthResponse(win.location.hash, win.location.search);
}
